import { useHandler } from "@redotech/react-util/hook";
import {
  StepType,
  stepTypeDisplayNames,
} from "@redotech/redo-model/advanced-flow/common";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { useCallback, useMemo } from "react";
import { wouldCreateCycle } from "../../utils/cycle-detection";
import { PartialFlowStep } from "../index";
import { stepTypes } from "../step-types";

/**
 * Hook to get available steps for connection dropdowns and create connection handlers
 * @param currentStep The current step being edited
 * @param allSteps All steps in the flow
 * @param setStep Function to update the current step
 * @param setHoveredNodeId Function to update hovered node (from parent)
 * @returns Object with availableSteps list and createConnectionHandler function
 */
export function useAvailableSteps<T extends PartialFlowStep>(
  currentStep: T,
  allSteps: PartialFlowStep[],
  setStep: (step: T) => void,
  setHoveredNodeId?: (nodeId: string | null) => void,
  unassignLabel?: string,
) {
  // Get available steps for dropdown, filtering out cycles and the current step
  const availableSteps = useMemo(() => {
    const unassignOption: RedoListItem<string> = {
      id: "unassign",
      type: "text",
      text: unassignLabel ?? "Unassign",
      value: "unassign",
    };

    const stepOptions = allSteps
      .filter((s) => {
        // Don't include the current step
        if (s.id === currentStep.id) {
          return false;
        }
        // Don't include trigger steps
        if (s.type === "trigger") {
          return false;
        }
        // Don't include end steps
        if (s.type === StepType.DO_NOTHING) {
          return false;
        }
        // Filter out steps that would create a cycle
        return !wouldCreateCycle(allSteps, currentStep.id, s.id);
      })
      .map(
        (s): RedoListItem<string> => ({
          id: s.id,
          type: "text",
          text:
            ("title" in s && s.title) || stepTypeDisplayNames[s.type] || s.type,
          value: s.id,
        }),
      );

    return [unassignOption, ...stepOptions];
  }, [allSteps, currentStep, unassignLabel]);

  /**
   * Creates a connection handler for a specific handle index
   * @param handleIndex The index of the handle (0 for true/next, 1 for false, etc.)
   * @returns Handler function that updates the connection
   */
  const createConnectionHandler = useHandler((handleIndex: number) => {
    return (stepId: string | undefined) => {
      const stepType = stepTypes[currentStep.type];
      if (stepType.type === "custom" && stepType.updateConnection) {
        if (!stepId) {
          return;
        }

        const targetStepId = stepId === "unassign" ? undefined : stepId;

        let currentConnectionValue: string | undefined;
        if (
          (currentStep.type === StepType.RULE_CONDITIONS ||
            currentStep.type === StepType.WORK_ORDER_CONDITION) &&
          "nextTrueId" in currentStep
        ) {
          currentConnectionValue =
            (handleIndex === 0
              ? currentStep.nextTrueId
              : currentStep.nextFalseId) ?? undefined;
        } else if ("nextId" in currentStep) {
          currentConnectionValue = currentStep.nextId ?? undefined;
        }

        if (targetStepId === currentConnectionValue) {
          return;
        }

        const updated = stepType.updateConnection(
          currentStep,
          handleIndex,
          targetStepId!,
        );
        setStep(updated as T);
      }
    };
  });

  const handleItemHover = useCallback(
    (stepId: string | null) => {
      if (!setHoveredNodeId) {
        return;
      }

      if (stepId === "unassign") {
        setHoveredNodeId(null);
      } else {
        setHoveredNodeId(stepId);
      }
    },
    [setHoveredNodeId],
  );

  return {
    availableSteps,
    createConnectionHandler,
    onItemHover: handleItemHover,
  };
}
